import { Box, SxProps, Theme } from '@mui/material'
import {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

const MIN_HEIGHT = 320
// The page body's bottom padding (PageLayout), kept clear below the grid.
const BOTTOM_GAP = 24

/**
 * Gives a data grid the height left between its top and the bottom of the
 * content area, so the grid scrolls inside itself in both directions and both
 * scrollbars stay in view.
 *
 * Without a bounded height a grid grows to fit every row, and its horizontal
 * scrollbar ends up below the last row, off-screen: wide tables looked as if
 * they couldn't scroll sideways. (BaseTable's old autoHeight was meant to do
 * this but looked its container up by an id with a '#' in it, so it never
 * applied.)
 *
 * `height` fixes the height instead. The frame re-measures on every render
 * (a note appearing above the grid moves it down), on window resize and when
 * the content area resizes; it never goes below 320px, and below that the
 * page itself scrolls.
 */
export const GridFrame = ({
  children,
  height,
  className,
  sx,
}: {
  children: ReactNode
  height?: number
  className?: string
  sx?: SxProps<Theme>
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [fitted, setFitted] = useState(MIN_HEIGHT)

  const measure = useCallback(() => {
    const el = ref.current
    if (!el || height) return
    const area = el.closest('main')
    let available: number
    if (area) {
      // Distance from the top of the content area's scrolled content, so a
      // scrolled page doesn't shrink or grow the grid.
      const top =
        el.getBoundingClientRect().top -
        area.getBoundingClientRect().top +
        area.scrollTop
      available = area.clientHeight - top - BOTTOM_GAP
    } else {
      available =
        window.innerHeight - el.getBoundingClientRect().top - BOTTOM_GAP
    }
    const next = Math.max(MIN_HEIGHT, Math.floor(available))
    setFitted((previous) => (previous === next ? previous : next))
  }, [height])

  // Every render: what sits above the grid may have changed.
  useLayoutEffect(() => {
    measure()
  })

  useLayoutEffect(() => {
    window.addEventListener('resize', measure)
    const area = ref.current?.closest('main')
    const observer =
      area && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => measure())
        : null
    if (area && observer) observer.observe(area)
    return () => {
      window.removeEventListener('resize', measure)
      observer?.disconnect()
    }
  }, [measure])

  return (
    <Box
      ref={ref}
      className={className}
      sx={[
        {
          height: height ?? fitted,
          width: '100%',
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  )
}
